import { createSlice } from "@reduxjs/toolkit";
import { AppProps, Thunk } from "store";
import {
  getClient,
  getClientFRRBids,
  getClientGOSettings,
  getClientMeteringPoints,
  getClients,
  getClientSupplyPlanForecast,
  getClientSupplyPlanPriceLimits,
  getGOCertificates,
} from "api/resources/clients";
import { getUsers, getUser } from "api/resources/users";
import {
  getContracts,
  getBaseLoads,
  getPortfolioContracts,
} from "api/resources/contracts";
import { Operator } from "api/models/Operator";
import { User } from "api/models/User";
import { ReportsQuery } from "api/models/Reports";
import { getMeteringPoints } from "api/resources/meteringPoints";
import { getOperators } from "api/resources/operators";
import {
  getAllReportsQueries,
  getReportsQueryOptions,
} from "api/resources/reports";
import { Forecast, ForecastTemplate } from "api/models/Forecast";
import {
  getAllForecasts,
  getAllForecastTemplates,
  getForecast,
} from "api/resources/forecasts";
import {
  getAllProductionParks,
  getAllProductionParksByClientId,
  getProductionParkById,
  getProductionParkLimitations,
  getSupplyPlanForecast,
} from "api/resources/productionPark";
import {
  getProductionForecastGroups,
  getProductionForecastGroup,
  getProductionPrognose,
  getProductionPrognoses,
  getProductionForecast,
} from "api/resources/productionForecasts";
import { ForecaCoefficientsData } from "api/models/Foreca";
import { getForecaCoefficents } from "api/resources/foreca";
import { getMe } from "api/resources/me";
import { getBalancePlans } from "api/resources/balancePlans";
import { getAllLitgridLogs } from "api/resources/litgrid";
import { InviteTokenData } from "api/models/Invite";
import { getUserInvites } from "api/resources/invites";
import {
  getSupplyPlanPriceLimits,
  getSupplyPlanScheduled,
} from "api/resources/supplyPlan";
import { FRRBid, GOCertificate } from "api/models/Client";
import {
  getPortfolios,
  getPortfolio,
  getPortfolioPriceLimits,
} from "api/resources/portfolios";
import { Portfolio, PriceLimit } from "api/models/Portfolio";
import {
  getNordPoolSupplyPlans,
  getNordPoolSupplyPlan,
  getNordPoolPriceSteps,
  updateNordPoolPriceSteps,
} from "api/resources/nordPool";
import { SupplyPlan } from "api/models/NordPool";
import {
  getGOGroups,
  getGOYearlySummary,
  getGOSummary,
  getLastGoSyncTime,
  getGOActiveClients,
  GOSummaryFilters,
} from "api/resources/go";
import {
  getBatteryGroups,
  getBatteryGroup,
} from "api/resources/batteryGroups";
import { getBatteries, getBattery } from "api/resources/batteries";

export const initialState: AppProps = {
  me: null,
  currentRoleClientId: null,
  invites: [],
  client: null,
  clientMeteringPoints: null,
  clients: null,
  users: [],
  user: null,
  operators: [],
  operator: null,
  meteringPoints: null,
  contracts: null,
  reportsQueryOptions: null,
  reportsQueries: [],
  forecastTemplates: null,
  forecasts: null,
  forecast: null,
  productionPark: null,
  productionParks: null,
  productionParkComponent: null,
  productionParkComponents: null,
  productionForecast: null,
  productionForecasts: [],
  productionForecastSelect: null,
  forecaCoefficients: [],
  litgridLogs: null,
  supplyPlanForecast: null,
  supplyPlanPriceLimits: null,
  baseLoads: null,
  supplyPlanScheduled: null,
  productionForecastGroups: [],
  productionForecastGroup: null,
  productionForecastNew: null,
  productionParkLimitations: [],
  frrBids: [],
  portfolios: [],
  portfolioPriceLimits: null,
  nordPoolSupplyPlans: [],
  nordPoolSupplyPlan: null,
  nordPoolPriceSteps: [],
  clientSupplyPlanForecast: null,
  portfolioContracts: null,
  clientGOSettings: null,
  GOCertificates: null,
  GOGroups: [],
  GOYearlySummary: null,
  GOSummary: null,
  lastGoSyncTime: null,
  GOActiveClients: [],
  batteryGroups: [],
  batteryGroup: null,
  batteries: [],
  battery: null,
  balancePlans: [],
};

export const appSlice = createSlice({
  name: "app",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    fetchMeReducer(state, data) {
      if (data) {
        state.me = {
          id: data.payload.id,
          firstName: data.payload.firstName,
          lastName: data.payload.lastName,
          email: data.payload.email,
          roles: data.payload.roles,
        };
        state.currentRoleClientId = data.payload.roles?.find(
          (d) => d.isCurrentRole,
        )?.client?.id;
      }
    },
    fetchInviteReducer(state, data) {
      if (data.payload) {
        state.invites = data.payload.map((invite: InviteTokenData) => ({
          invitedBy: invite.invitedBy,
          email: invite.email,
          clientName: invite.clientName,
          roleName: invite.roleName,
          inviteDateTime: invite.inviteDateTime,
          accepted: invite.accepted,
          inviteToken: invite.inviteToken,
        }));
      }
    },
    fetchAllClientsReducer(state, data) {
      if (data.payload) {
        state.clients = {
          totalElements: data.payload.totalElements,
          totalPages: data.payload.totalPages,
          size: data.payload.size,
          content: data.payload.content,
        };
      }
    },
    fetchClientReducer(state, data) {
      const client = data.payload;

      if (client) {
        state.client = {
          id: client.id,
          firstName: client.firstName,
          lastName: client.lastName,
          companyName: client.companyName,
          email: client.email,
          phone: client.phone,
          eic: client.eic,
          type: client.type,
          registryCode: client.registryCode,
          personalCode: client.personalCode,
          country: client.country,
          county: client.county,
          city: client.city,
          address: client.address,
          state: client.state,
          zipCode: client.zipCode,
          meteringPointsCount: client.meteringPointsCount,
          iban: client.iban,
          bankName: client.bankName,
          linkedUsers: client.linkedUsers,
          invitedUsers: client.invitedUsers,
          contractType: client.contractType,
        };
      }
    },
    fetchClientMeteringPointsReducer(state, data) {
      if (data.payload) {
        state.clientMeteringPoints = {
          totalElements: data.payload.totalElements,
          totalPages: data.payload.totalPages,
          size: data.payload.size,
          content: data.payload.content,
        };
      }
    },
    fetchClientFRRBidsReducer(state, data) {
      state.frrBids = data.payload.map((bid: FRRBid) => {
        return {
          processType: bid.processType,
          senderEic: bid.senderEic,
          createdDateTime: bid.createdDateTime,
          reserveBidTimeIntervalStart: bid.reserveBidTimeIntervalStart,
          reserveBidTimeIntervalEnd: bid.reserveBidTimeIntervalEnd,
          subjectEic: bid.subjectEic,
          providerEic: bid.providerEic,
          divisibleType: bid.divisibleType,
          linkedBidsId: bid.linkedBidsId,
          multipartBidId: bid.multipartBidId,
          exclusiveBidId: bid.exclusiveBidId,
          status: bid.status,
        };
      });
    },
    fetchAllUsersReducer(state, data) {
      state.users = data.payload.map((user: User) => {
        return {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          linkedClients: user.linkedClients,
        };
      });
    },
    fetchUserReducer(state, data) {
      if (data.payload) {
        state.user = {
          id: data.payload.id,
          firstName: data.payload.firstName,
          lastName: data.payload.lastName,
          email: data.payload.email,
          linkedClients: data.payload.linkedClients,
        };
      }
    },
    fetchAllOperatorsReducer(state, data) {
      state.operators = data.payload.map((operator: Operator) => {
        return {
          id: operator.id,
          eic: operator.eic,
          name: operator.name,
          registryCode: operator.registryCode,
        };
      });
    },
    fetchAllMeteringPointsReducer(state, data) {
      if (data.payload) {
        state.meteringPoints = {
          totalElements: data.payload.totalElements,
          totalPages: data.payload.totalPages,
          size: data.payload.size,
          content: data.payload.content,
        };
      }
    },
    fetchAllContractsReducer(state, data) {
      if (data.payload) {
        state.contracts = {
          totalElements: data.payload.totalElements,
          totalPages: data.payload.totalPages,
          size: data.payload.size,
          content: data.payload.content,
        };
      }
    },
    fetchReportsQueryOptionsReducer(state, data) {
      if (data.payload) {
        state.reportsQueryOptions = {
          periodPresets: data.payload.periodPresets,
          readingTypes: data.payload.readingTypes,
          readingsSumType: data.payload.readingsSumType,
        };
      }
    },
    fetchAllReportsQueriesReducer(state, data) {
      state.reportsQueries = data.payload.map((query: ReportsQuery) => {
        return {
          startDate: query.startDate,
          endDate: query.endDate,
          readingsSumType: query.readingsSumType,
          clients: query.clients,
          meteringPoints: query.meteringPoints,
          contractStartDate: query.contractStartDate,
          contractEndDate: query.contractEndDate,
          name: query.name,
          id: query.id,
        };
      });
    },
    fetchAllForecastTemplatesReducer(state, data) {
      state.forecastTemplates = data.payload.map(
        (template: ForecastTemplate) => {
          return {
            id: template.id,
            name: template.name,
            filters: template.filters,
          };
        },
      );
    },
    fetchAllForecastsReducer(state, data) {
      state.forecasts = data.payload.map((forecast: Forecast) => {
        return {
          id: forecast.id,
          name: forecast.name,
          prognoseDate: forecast.prognoseDate,
          isTemplate: forecast.isTemplate,
          isVerified: forecast.isVerified,
          templateId: forecast.templateId,
          createdByUser: forecast.createdByUser,
          updatedByUser: forecast.updatedByUser,
        };
      });
    },
    fetchForecastReducer(state, data) {
      if (data.payload) {
        state.forecast = {
          id: data.payload.id,
          name: data.payload.name,
          prognoseDate: data.payload.prognoseDate,
          meteringPointsCount: data.payload.meteringPointsCount,
          isVerified: data.payload.isVerified,
          customDates: data.payload.customDates,
          filtersJson: data.payload.filtersJson,
          cellsJson: data.payload.cellsJson,
          forecastTemperatures: data.payload.forecastTemperatures,
          measuredTemperatures: data.payload.measuredTemperatures,
          temperatureMatches: data.payload.temperatureMatches,
          prognoseValues: data.payload.prognoseValues,
          parentId: data.payload.parentId,
          createdByUser: data.payload.createdByUser,
          updatedByUser: data.payload.updatedByUser,
          isTemplate: data.payload.isTemplate,
          templateId: data.payload.templateId,
        };
      }
    },

    fetchAllProductionParksReducer(state, data) {
      if (data.payload) {
        state.productionParks = {
          totalElements: data.payload.totalElements,
          totalPages: data.payload.totalPages,
          size: data.payload.size,
          content: data.payload.content,
        };
      }
    },

    fetchProductionParkReducer(state, data) {
      if (data.payload) {
        state.productionPark = {
          id: data.payload.id,
          name: data.payload.name,
          code: data.payload.code,
          aiolosCode: data.payload.aiolosCode,
          location: data.payload.location,
          priceArea: data.payload.priceArea,
          deliveryType: data.payload.deliveryType,
          type: data.payload.type,
          numberOfInverters: data.payload.numberOfInverters,
          maxPowerOfInverters: data.payload.maxPowerOfInverters,
          numberOfSolarPanels: data.payload.numberOfSolarPanels,
          maxPowerOfSolarPanels: data.payload.maxPowerOfSolarPanels,
          numberOfWindGenerators: data.payload.numberOfWindGenerators,
          maxPowerOfWindGenerators: data.payload.maxPowerOfWindGenerators,
          clientId: data.payload.clientId,
          meteringPoints: data.payload.meteringPoints,
          productionParkComponents: data.payload.productionParkComponents,
          consumptionForecastTemplate: data.payload.consumptionForecastTemplate,
          useBatteriesForSupplyPlan: data.payload.useBatteriesForSupplyPlan,
          isBigProducer: data.payload.isBigProducer,
        };
      }
    },
    fetchProductionPrognoseReducer(state, action) {
      state.productionForecast = action.payload;
    },
    fetchProductionPrognosesReducer(state, action) {
      state.productionForecasts = action.payload;
    },
    fetchForecaCoefficentsReducer(state, data) {
      state.forecaCoefficients = data.payload.map(
        (forecaDataRow: ForecaCoefficientsData) => {
          return {
            code: forecaDataRow.code,
            symbol: forecaDataRow.symbol,
            coefficient: forecaDataRow.coefficient,
          };
        },
      );
    },
    fetchLitgridLogsReducer(state, action) {
      state.litgridLogs = action.payload;
    },
    fetchSupplyPlanForecastReducer(state, action) {
      state.supplyPlanForecast = action.payload;
    },
    fetchClientSupplyPlanForecastReducer(state, action) {
      state.clientSupplyPlanForecast = action.payload;
    },
    fetchSupplyPlanPriceLimitsReducer(state, action) {
      state.supplyPlanPriceLimits = action.payload;
    },
    fetchClientSupplyPlanPriceLimitsReducer(state, action) {
      state.clientSupplyPlanPriceLimits = action.payload;
    },
    fetchBaseLoadsReducer(state, action) {
      state.baseLoads = action.payload;
    },
    fetchSupplyPlanScheduledReducer(state, action) {
      state.supplyPlanScheduled = action.payload;
    },
    fetchForecastGroupsReducer(state, data) {
      state.productionForecastGroups = data.payload.map(
        (forecastGroupRow: any) => {
          return {
            id: forecastGroupRow.id,
            forecastDate: forecastGroupRow.forecastDate,
            createdAt: forecastGroupRow.createdAt,
            forecasts: forecastGroupRow.forecasts,
          };
        },
      );
    },
    fetchForecastGroupReducer(state, action) {
      state.productionForecastGroup = action.payload;
    },
    fetchProductionForecastReducer(state, action) {
      state.productionForecastNew = action.payload;
    },
    fetchProductionParkLimitationsReducer(state, data) {
      state.productionParkLimitations =
        data?.payload?.length > 0 &&
        data.payload.map((limitation: any) => {
          return {
            id: limitation.id,
            forecastType: limitation.forecastType,
            type: limitation.type,
            value: limitation.value,
            fromDateTime: limitation.fromDateTime,
            toDateTime: limitation.toDateTime,
            createdAt: limitation.createdAt,
            changeHistory: limitation.changeHistory,
          };
        });
    },
    fetchPortfoliosReducer(state, data) {
      state.portfolios = data.payload.map((portfolio: Portfolio) => {
        return {
          id: portfolio.id,
          name: portfolio.name,
          clients: portfolio.clients,
          nordpoolNames: portfolio.nordpoolNames,
          prognoseTemplates: portfolio.prognoseTemplates,
          sellPriceLimit: portfolio.sellPriceLimit,
          buyPriceLimit: portfolio.buyPriceLimit,
        };
      });
    },
    fetchPortfolioReducer(state, action) {
      state.portfolio = action.payload;
    },
    fetchPortfolioPriceLimitsReducer(state, data) {
      state.portfolioPriceLimits = data.payload
        ? data.payload.map((priceLimit: PriceLimit) => {
            return {
              time: priceLimit.time,
              buy: priceLimit.buyPriceLimit,
              sell: priceLimit.sellPriceLimit,
            };
          })
        : null;
    },
    fetchNordPoolSupplyPlansReducer(state, data) {
      state.nordPoolSupplyPlans = data.payload.map((supplyPlan: SupplyPlan) => {
        return {
          id: supplyPlan.id,
          portfolioId: supplyPlan.portfolioId,
          portfolioName: supplyPlan.portfolioName,
          nordpoolAuctionTypeId: supplyPlan.nordpoolAuctionTypeId,
          nordpoolAuctionTypeName: supplyPlan.nordpoolAuctionTypeName,
          status: supplyPlan.status,
          currentAuctionMetrics: supplyPlan.currentAuctionMetrics,
          previousAuctionMetrics: supplyPlan.previousAuctionMetrics,
          targetDate: supplyPlan.targetDate,
          createdAt: supplyPlan.createdAt,
          subPlans: supplyPlan.subPlans,
        };
      });
    },
    fetchNordPoolSupplyPlanReducer(state, action) {
      state.nordPoolSupplyPlan = action.payload;
    },
    fetchNordPoolPriceStepsReducer(state, action) {
      const values: number[] = Array.isArray(action.payload)
        ? action.payload
        : [];
      state.nordPoolPriceSteps = Array.from(new Set(values)).sort(
        (a, b) => a - b,
      );
    },
    fetchPortfolioContractsReducer(state, action) {
      state.portfolioContracts = action.payload;
    },
    fetchClientGOSettingsReducer(state, action) {
      state.clientGOSettings = action.payload;
    },
    fetchGOCertificatesReducer(state, data) {
      state.GOCertificates = {
        totalElements: data.payload.totalElements,
        totalPages: data.payload.totalPages,
        content: data.payload.content.map((certificate: GOCertificate) => {
          return {
            id: certificate.id,
            eleringId: certificate.eleringId,
            client: {
              id: certificate.client.id,
              name: certificate.client.name,
            },
            certificateNumberStart: certificate.certificateNumberStart,
            certificateNumberEnd: certificate.certificateNumberEnd,
            country: certificate.country,
            eecsCode: certificate.eecsCode,
            eicWCode: certificate.eicWCode,
            fuel: {
              code: certificate.fuel.code,
              name: certificate.fuel.name,
            },
            productionStart: certificate.productionStart,
            productionEnd: certificate.productionEnd,
            issueDate: certificate.issueDate,
            expiryDate: certificate.expiryDate,
            productionSupport: certificate.productionSupport,
            investmentSupport: certificate.investmentSupport,
            amount: certificate.amount,
            technology: {
              code: certificate.technology.code,
              name: certificate.technology.name,
            },
            productionDeviceName: certificate.productionDeviceName,
            productionDeviceOutput: certificate.productionDeviceOutput,
            deviceLocation: certificate.deviceLocation,
            transferDeadline: certificate.transferDeadline,
          };
        }),
      };
    },
    fetchGOGroupsReducer(state, action) {
      state.GOGroups = action.payload;
    },
    fetchGOYearlySummaryReducer(state, action) {
      state.GOYearlySummary = action.payload;
    },
    fetchGOSummaryReducer(state, action) {
      state.GOSummary = action.payload;
    },
    fetchLastGoSyncTimeReducer(state, action) {
      state.lastGoSyncTime = action.payload;
    },
    fetchGOActiveClientsReducer(state, action) {
      state.GOActiveClients = action.payload;
    },
    fetchBatteryGroupsReducer(state, action) {
      state.batteryGroups = action.payload;
    },
    fetchBatteryGroupReducer(state, action) {
      state.batteryGroup = action.payload;
    },
    fetchBatteriesReducer(state, action) {
      state.batteries = action.payload;
    },
    fetchBatteryReducer(state, action) {
      state.battery = action.payload;
    },
    fetchBalancePlansReducer(state, action) {
      state.balancePlans = action.payload;
    },
  },
});

export const {
  fetchMeReducer,
  fetchInviteReducer,
  fetchAllClientsReducer,
  fetchClientReducer,
  fetchAllUsersReducer,
  fetchAllMeteringPointsReducer,
  fetchClientMeteringPointsReducer,
  fetchClientFRRBidsReducer,
  fetchAllContractsReducer,
  fetchAllOperatorsReducer,
  fetchReportsQueryOptionsReducer,
  fetchAllReportsQueriesReducer,
  fetchAllForecastTemplatesReducer,
  fetchAllForecastsReducer,
  fetchForecastReducer,
  fetchAllProductionParksReducer,
  fetchProductionParkReducer,
  fetchProductionPrognoseReducer,
  fetchProductionPrognosesReducer,
  fetchForecaCoefficentsReducer,
  fetchLitgridLogsReducer,
  fetchUserReducer,
  fetchSupplyPlanForecastReducer,
  fetchSupplyPlanPriceLimitsReducer,
  fetchClientSupplyPlanPriceLimitsReducer,
  fetchBaseLoadsReducer,
  fetchSupplyPlanScheduledReducer,
  fetchForecastGroupsReducer,
  fetchForecastGroupReducer,
  fetchProductionForecastReducer,
  fetchProductionParkLimitationsReducer,
  fetchPortfoliosReducer,
  fetchPortfolioReducer,
  fetchPortfolioPriceLimitsReducer,
  fetchNordPoolSupplyPlansReducer,
  fetchNordPoolSupplyPlanReducer,
  fetchNordPoolPriceStepsReducer,
  fetchClientSupplyPlanForecastReducer,
  fetchPortfolioContractsReducer,
  fetchClientGOSettingsReducer,
  fetchGOCertificatesReducer,
  fetchGOGroupsReducer,
  fetchGOYearlySummaryReducer,
  fetchGOSummaryReducer,
  fetchLastGoSyncTimeReducer,
  fetchGOActiveClientsReducer,
  fetchBatteryGroupsReducer,
  fetchBatteryGroupReducer,
  fetchBatteriesReducer,
  fetchBatteryReducer,
  fetchBalancePlansReducer,
} = appSlice.actions;

export const fetchInvites = (): Thunk => (dispatch) => {
  getUserInvites()
    .then((data) => {
      dispatch(fetchInviteReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchMe = (): Thunk => (dispatch) => {
  getMe()
    .then((data) => {
      dispatch(fetchMeReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchAllClientsWithFilters =
  (page: number, size: number, filters: { [key: string]: string }): Thunk =>
  (dispatch) => {
    getClients(page, size, filters)
      .then(({ data }) => {
        dispatch(fetchAllClientsReducer(data));
      })
      .catch((err) => console.log(err));
  };

export const fetchAllClients =
  (page: number, size: number): Thunk =>
  (dispatch) => {
    getClients(page, size)
      .then(({ data }) => {
        dispatch(fetchAllClientsReducer(data));
      })
      .catch((err) => console.log(err));
  };

export const fetchClient =
  (clientId: string): Thunk =>
  (dispatch, getState) => {
    const current = getState().app.client?.id === Number(clientId);
    if (!current) {
      dispatch(
        fetchClientReducer({
          id: null,
          firstName: "",
          lastName: "",
          companyName: "",
          email: "",
          phone: "",
          eic: "",
          type: "",
          registryCode: "",
          personalCode: "",
          country: "",
          county: "",
          city: "",
          address: "",
          state: "",
          zipCode: "",
          iban: "",
          bankName: "",
          meteringPointsCount: 0,
          linkedUsers: [],
          invitedUsers: [],
        }),
      );
    }

    getClient(clientId)
      .then(({ data }) => {
        dispatch(fetchClientReducer(data));
      })
      .catch((err) => console.log(err));
  };

export const fetchClientMeteringPoints =
  (clientId: string, page: number, size: number): Thunk =>
  (dispatch) => {
    fetchClientMeteringPointsReducer(null);

    getClientMeteringPoints(clientId, page, size)
      .then(({ data }) => {
        dispatch(fetchClientMeteringPointsReducer(data));
      })
      .catch((err) => console.log(err));
  };

export const fetchClientFRRBids =
  (
    clientId: number,
    filters: {
      uploadedAtFrom?: string | null;
      uploadedAtTo?: string | null;
      processType?: string | null;
      reserve_bid_timeinterval_start?: string | null;
      reserve_bid_timeinterval_end?: string | null;
    },
  ): Thunk =>
  (dispatch) => {
    getClientFRRBids(clientId, filters)
      .then(({ data }) => {
        dispatch(fetchClientFRRBidsReducer(data));
      })
      .catch((err) => console.log(err));
  };

export const fetchAllUsers = (): Thunk => (dispatch) => {
  getUsers()
    .then(({ data }) => {
      dispatch(fetchAllUsersReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchUser =
  (userId: number): Thunk =>
  (dispatch) => {
    getUser(userId)
      .then(({ data }) => {
        dispatch(fetchUserReducer(data));
      })
      .catch((err) => console.log(err));
  };

export const fetchAllOperators = (): Thunk => (dispatch) => {
  getOperators()
    .then(({ data }) => {
      dispatch(fetchAllOperatorsReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchAllMeteringPoints =
  (page: number, size: number, filters: { [key: string]: string }): Thunk =>
  (dispatch) => {
    getMeteringPoints(page, size, filters)
      .then(({ data }) => {
        dispatch(fetchAllMeteringPointsReducer(data));
      })
      .catch((err) => console.log(err));
  };

export const fetchAllContracts =
  (page: number, size: number, filters: { [key: string]: string }): Thunk =>
  (dispatch) => {
    getContracts(page, size, filters)
      .then(({ data }) => {
        dispatch(fetchAllContractsReducer(data));
      })
      .catch((err) => console.log(err));
  };

export const fetchReportsQueryOptions = (): Thunk => (dispatch) => {
  getReportsQueryOptions()
    .then(({ data }) => {
      dispatch(fetchReportsQueryOptionsReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchAllReportsQueries = (): Thunk => (dispatch) => {
  getAllReportsQueries()
    .then(({ data }) => {
      dispatch(fetchAllReportsQueriesReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchAllForecastTemplates = (): Thunk => (dispatch) => {
  getAllForecastTemplates()
    .then(({ data }) => {
      dispatch(fetchAllForecastTemplatesReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchAllForecasts = (): Thunk => (dispatch) => {
  getAllForecasts()
    .then(({ data }) => {
      dispatch(fetchAllForecastsReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchForecast =
  (forecastId: string): Thunk =>
  (dispatch) => {
    getForecast(forecastId)
      .then(({ data }) => {
        dispatch(fetchForecastReducer(data));
      })
      .catch((err) => console.log(err));
  };

export const fetchAllProductionPark =
  (page: number, size: number, filters: { [key: string]: string }): Thunk =>
  (dispatch) => {
    dispatch(
      fetchAllProductionParksReducer({
        totalElements: 0,
        totalPages: 0,
        size: 0,
        content: [],
      }),
    );

    getAllProductionParks(page, size, filters)
      .then(({ data }) => {
        dispatch(fetchAllProductionParksReducer(data));
      })
      .catch((err) => console.log(err));
  };

export const fetchAllProductionParkByClientId = (): Thunk => (dispatch) => {
  dispatch(
    fetchAllProductionParksReducer({
      totalElements: 0,
      totalPages: 0,
      size: 0,
      content: [],
    }),
  );

  getAllProductionParksByClientId()
    .then(({ data }) => {
      dispatch(fetchAllProductionParksReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchProductionPark =
  (productionParkId: number): Thunk =>
  (dispatch) => {
    dispatch(
      fetchProductionParkReducer({
        id: null,
        name: "",
        code: "",
        aiolosCode: "",
        location: "",
        priceArea: "",
        type: "",
        numberOfInverters: 0,
        maxPowerOfInverters: 0,
        numberOfSolarPanels: 0,
        maxPowerOfSolarPanels: 0,
        numberOfWindGenerators: 0,
        maxPowerOfWindGenerators: 0,
        clientId: "",
        meteringPoints: [],
        productionParkComponents: [],
      }),
    );

    getProductionParkById(productionParkId)
      .then(({ data }) => {
        dispatch(fetchProductionParkReducer(data));
      })
      .catch((err) => console.log(err));
  };

export const fetchForecaCoefficents = (): Thunk => (dispatch) => {
  getForecaCoefficents()
    .then(({ data }) => {
      dispatch(fetchForecaCoefficentsReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchProductionPrognose =
  (id: number): Thunk =>
  async (dispatch) => {
    try {
      dispatch(fetchProductionPrognoseReducer(null));

      const prognose = await getProductionPrognose(id);

      if (prognose.filtersJson.length > 0) {
        const productionParkIds = prognose.filtersJson[0].productionParks;
        const promises = productionParkIds.map((parkId) =>
          getProductionParkById(parkId),
        );
        const results = await Promise.all(promises);

        const productionParks = results.map((result) => result.data);
        const transformedPrognose = {
          ...prognose,
          filtersJson: [
            {
              ...prognose.filtersJson[0],
              productionParks: productionParks,
            },
          ],
        };

        dispatch(fetchProductionPrognoseReducer(transformedPrognose));
      } else {
        dispatch(fetchProductionPrognoseReducer(prognose));
      }
    } catch (error) {
      console.error(error);
    }
  };

export const fetchProductionPrognoses =
  (date?: string): Thunk =>
  async (dispatch) => {
    try {
      const { data } = await getProductionPrognoses(date);
      dispatch(fetchProductionPrognosesReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchLitgridLogs = (): Thunk => async (dispatch) => {
  try {
    const { data } = await getAllLitgridLogs();
    dispatch(fetchLitgridLogsReducer(data));
  } catch (error) {
    console.error(error);
  }
};

export const fetchSupplyPlanForecast =
  (productionParkId: number, auctionTypeId: number, date: string): Thunk =>
  async (dispatch) => {
    dispatch(fetchSupplyPlanForecastReducer(null));

    try {
      const { data } = await getSupplyPlanForecast(
        productionParkId,
        auctionTypeId,
        date,
      );
      dispatch(fetchSupplyPlanForecastReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchClientSupplyPlanForecast =
  (
    clientId: number,
    auctionTypeId: number,
    date: string,
    selectedProductionPark?: number,
  ): Thunk =>
  async (dispatch) => {
    dispatch(fetchClientSupplyPlanForecastReducer(null));

    try {
      const { data } = await getClientSupplyPlanForecast(
        clientId,
        auctionTypeId,
        date,
        selectedProductionPark,
      );
      dispatch(fetchClientSupplyPlanForecastReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchSupplyPlanPriceLimits =
  (productionParkId: number, auctionId: number): Thunk =>
  async (dispatch) => {
    try {
      const { data } = await getSupplyPlanPriceLimits(
        productionParkId,
        auctionId,
      );
      dispatch(fetchSupplyPlanPriceLimitsReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchClientSupplyPlanPriceLimits =
  (
    clientId: number,
    auctionId: number,
    selectedProductionPark?: number,
  ): Thunk =>
  async (dispatch) => {
    try {
      const { data } = await getClientSupplyPlanPriceLimits(
        clientId,
        auctionId,
        selectedProductionPark,
      );
      dispatch(fetchClientSupplyPlanPriceLimitsReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchBaseLoads =
  (clientId: number): Thunk =>
  async (dispatch) => {
    try {
      const { data } = await getBaseLoads(clientId);
      dispatch(fetchBaseLoadsReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchSupplyPlanScheduled =
  (clientId: number, auctionTypeId: number, date: string): Thunk =>
  async (dispatch) => {
    dispatch(fetchSupplyPlanScheduledReducer(null));

    try {
      const { data } = await getSupplyPlanScheduled(
        clientId,
        auctionTypeId,
        date,
      );
      dispatch(fetchSupplyPlanScheduledReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchProductionForecastGroups =
  (fromDate: string, toDate: string): Thunk =>
  async (dispatch) => {
    try {
      const { data } = await getProductionForecastGroups(fromDate, toDate);
      dispatch(fetchForecastGroupsReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchProductionForecastGroup =
  (id: number, filters: any): Thunk =>
  async (dispatch) => {
    dispatch(fetchForecastGroupReducer(null));

    try {
      const { data } = await getProductionForecastGroup(id, filters, true);
      dispatch(fetchForecastGroupReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchProductionForecast =
  (id: number): Thunk =>
  async (dispatch) => {
    try {
      const { data } = await getProductionForecast(id);
      dispatch(fetchProductionForecastReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchProductionParkLimitations =
  (
    productionParkId: number,
    fromDateTime?: string,
    toDateTime?: string,
  ): Thunk =>
  async (dispatch) => {
    dispatch(fetchProductionParkLimitationsReducer(null));

    if (productionParkId === null) return;

    try {
      const { data } = await getProductionParkLimitations(
        productionParkId,
        fromDateTime,
        toDateTime,
      );
      dispatch(fetchProductionParkLimitationsReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchPortfolios = (): Thunk => async (dispatch) => {
  try {
    const { data } = await getPortfolios();
    dispatch(fetchPortfoliosReducer(data));
  } catch (error) {
    console.error(error);
  }
};

export const fetchPortfolio =
  (portfolioId: number): Thunk =>
  async (dispatch) => {
    try {
      const { data } = await getPortfolio(portfolioId);
      dispatch(fetchPortfolioReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchPortfolioPriceLimits =
  (portfolioId: number, auctionTypeId: number): Thunk =>
  async (dispatch) => {
    try {
      const { data } = await getPortfolioPriceLimits(
        portfolioId,
        auctionTypeId,
      );
      dispatch(fetchPortfolioPriceLimitsReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchNordPoolSupplyPlans =
  (fromDate: string, toDate: string): Thunk =>
  async (dispatch) => {
    try {
      const { data } = await getNordPoolSupplyPlans(fromDate, toDate);
      dispatch(fetchNordPoolSupplyPlansReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchNordPoolSupplyPlan =
  (nordPoolSupplyPlanId: number): Thunk =>
  async (dispatch) => {
    try {
      const { data } = await getNordPoolSupplyPlan(nordPoolSupplyPlanId);
      dispatch(fetchNordPoolSupplyPlanReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchNordPoolPriceSteps = (): Thunk => async (dispatch) => {
  try {
    const { data } = await getNordPoolPriceSteps();
    dispatch(fetchNordPoolPriceStepsReducer(data));
  } catch (error) {
    console.error(error);
  }
};

export const saveNordPoolPriceSteps =
  (values: number[]): Thunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const { data } = await updateNordPoolPriceSteps(values);
      dispatch(fetchNordPoolPriceStepsReducer(data ?? values));
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

export const fetchPortfolioContracts =
  (clientId: number): Thunk =>
  async (dispatch) => {
    try {
      const { data } = await getPortfolioContracts(clientId);
      dispatch(fetchPortfolioContractsReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchClientGOSettings =
  (clientId: number): Thunk =>
  async (dispatch) => {
    dispatch(fetchClientGOSettingsReducer(null));

    try {
      const { data } = await getClientGOSettings(clientId);
      dispatch(fetchClientGOSettingsReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchGOCertificates =
  (page: number, size: number, filters: any): Thunk =>
  (dispatch) => {
    getGOCertificates(page, size, filters)
      .then(({ data }) => {
        dispatch(fetchGOCertificatesReducer(data));
      })
      .catch((err) => console.log(err));
  };

export const fetchGOGroups = (): Thunk => (dispatch) => {
  getGOGroups()
    .then(({ data }) => {
      dispatch(fetchGOGroupsReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchBatteryGroups = (): Thunk => (dispatch) => {
  getBatteryGroups()
    .then(({ data }) => {
      dispatch(fetchBatteryGroupsReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchBatteryGroup = (id: number): Thunk => (dispatch) => {
  getBatteryGroup(id)
    .then(({ data }) => {
      dispatch(fetchBatteryGroupReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchBatteries = (): Thunk => (dispatch) => {
  getBatteries()
    .then(({ data }) => {
      dispatch(fetchBatteriesReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchBattery = (id: number): Thunk => (dispatch) => {
  getBattery(id)
    .then(({ data }) => {
      dispatch(fetchBatteryReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchGOYearlySummary = (): Thunk => (dispatch) => {
  getGOYearlySummary()
    .then(({ data }) => {
      dispatch(fetchGOYearlySummaryReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchGOSummary =
  (filters?: GOSummaryFilters): Thunk =>
  (dispatch) => {
    getGOSummary(filters)
      .then(({ data }) => {
        dispatch(fetchGOSummaryReducer(data));
      })
      .catch((err) => console.log(err));
  };

export const fetchLastGoSyncTime = (): Thunk => (dispatch) => {
  getLastGoSyncTime()
    .then(({ data }) => {
      dispatch(fetchLastGoSyncTimeReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchGOActiveClients = (): Thunk => (dispatch) => {
  getGOActiveClients()
    .then(({ data }) => {
      dispatch(fetchGOActiveClientsReducer(data));
    })
    .catch((err) => console.log(err));
};

export const fetchBalancePlans =
  (startDate: string, endDate: string): Thunk =>
  async (dispatch) => {
    try {
      const { data } = await getBalancePlans(startDate, endDate);
      dispatch(fetchBalancePlansReducer(data));
    } catch (error) {
      console.error(error);
    }
  };

export default appSlice.reducer;
